import { icons } from '../assets/icons';
import { NewsService, NewsPost } from '../services/news';
import { PROJECT_CONFIG } from '../config/project-info';
import { GiscusWidget } from './GiscusWidget';

export class NewsPostViewComponent {
  private currentSlug: string = '';
  private post: NewsPost | null = null;
  private giscus: GiscusWidget | null = null;

  public loadPost(slug: string) {
    if (this.giscus) {
      this.giscus.destroy();
      this.giscus = null;
    }

    this.currentSlug = slug;
    this.post = NewsService.getPostBySlug(slug);
    this.updateView();
  }

  public render(): string {
    if (!this.post) {
      return `
        <div id="news-post-container" class="min-h-screen pt-12 pb-20 font-mono text-left">
          <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
            <div class="w-14 h-14 rounded-full bg-rose-950 text-rose-500 flex items-center justify-center mx-auto">
              ${icons.alertCircle('w-8 h-8')}
            </div>
            <h1 class="text-2xl sm:text-4xl font-extrabold text-white">Article Not Found</h1>
            <p class="text-xs sm:text-sm text-terminal-muted max-w-md mx-auto">
              The article you are looking for (<code>${this.currentSlug}</code>) does not exist or has been moved.
            </p>
            <div class="pt-2">
              <a 
                href="#/news" 
                class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs bg-matrix-500 text-terminal-bgDark hover:bg-matrix-400 transition-colors"
              >
                <span>← Back to All News</span>
              </a>
            </div>
          </div>
        </div>
      `;
    }

    const { prev, next } = NewsService.getAdjacentPosts(this.post.slug);

    // Initialize Giscus for this specific post
    this.giscus = new GiscusWidget({
      containerId: 'news-post-giscus-container',
      mapping: 'specific',
      term: `News: ${this.post.title}`,
      category: 'General',
      inputPosition: 'top'
    });

    return `
      <div id="news-post-container" class="min-h-screen pt-6 pb-20 font-mono text-left">
        <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          <!-- Top Breadcrumb & Share Actions -->
          <div class="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-terminal-border text-xs">
            <div class="flex items-center gap-2 text-terminal-muted">
              <a href="#/news" class="hover:text-matrix-400 transition-colors flex items-center gap-1">
                <span>News</span>
              </a>
              <span>/</span>
              <span class="text-matrix-400 truncate max-w-[200px] sm:max-w-[360px]">${this.post.title}</span>
            </div>

            <div class="flex items-center gap-2">
              <button 
                id="post-copy-link-btn"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-terminal-card hover:bg-terminal-cardLight text-matrix-400 hover:text-matrix-300 border border-terminal-border hover:border-matrix-500 transition-all cursor-pointer"
                title="Copy article link"
              >
                <span id="post-copy-icon">${icons.copy('w-3.5 h-3.5')}</span>
                <span id="post-copied-icon" class="hidden text-matrix-300">${icons.check('w-3.5 h-3.5')}</span>
                <span id="post-copy-text">Copy Link</span>
              </button>

              <a 
                href="${PROJECT_CONFIG.mastodonUrl}"
                target="_blank"
                rel="me noopener noreferrer"
                class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyber-950/80 hover:bg-cyber-900 text-cyber-300 border border-cyber-700 transition-colors"
                title="Discuss on Mastodon"
              >
                ${icons.mastodon('w-3.5 h-3.5 text-cyber-400')}
                <span>Discuss</span>
              </a>
            </div>
          </div>

          <!-- Article Header -->
          <header class="space-y-4">
            
            <div class="flex flex-wrap items-center gap-2">
              ${this.post.tags.map(t => `
                <a href="#/news" class="px-2.5 py-0.5 rounded text-xs font-semibold bg-matrix-950 text-matrix-400 border border-matrix-800 hover:border-matrix-500">
                  #${t}
                </a>
              `).join('')}
              <span class="text-xs text-terminal-muted">•</span>
              <span class="text-xs text-terminal-muted">${this.post.date}</span>
              <span class="text-xs text-terminal-muted">•</span>
              <span class="text-xs text-cyber-400 font-semibold">${this.post.readingTime}</span>
            </div>

            <h1 class="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
              ${this.post.title}
            </h1>

            <p class="text-sm sm:text-base text-terminal-muted leading-relaxed font-medium">
              ${this.post.summary}
            </p>

            <!-- Author byline -->
            <div class="flex items-center gap-3 pt-2">
              ${this.post.authorAvatar ? `
                <img src="${this.post.authorAvatar}" alt="${this.post.author}" class="w-10 h-10 rounded-full border border-matrix-500 shadow-glow-sm object-cover" />
              ` : ''}
              <div>
                <div class="text-xs font-bold text-white">
                  ${this.post.authorUrl ? `
                    <a href="${this.post.authorUrl}" target="_blank" rel="noopener noreferrer" class="hover:text-matrix-400 underline decoration-matrix-700">
                      ${this.post.author}
                    </a>
                  ` : this.post.author}
                </div>
                <div class="text-[11px] text-terminal-muted">Lead Maintainer • Mastui Project</div>
              </div>
            </div>

            ${this.post.coverImage ? `
              <div class="rounded-2xl overflow-hidden border border-terminal-border bg-black max-h-[440px] mt-6">
                <img src="${this.post.coverImage}" alt="${this.post.title}" class="w-full h-full object-cover" />
              </div>
            ` : ''}

          </header>

          <!-- Article Body (.prose-custom) -->
          <main class="p-6 sm:p-10 rounded-2xl bg-terminal-card border border-terminal-border shadow-glow-lg prose-custom max-w-none">
            ${this.post.htmlContent}
          </main>

          <!-- Author Bio Card -->
          <div class="p-6 rounded-2xl bg-terminal-card border border-terminal-border flex flex-col sm:flex-row items-center sm:items-start gap-4">
            ${this.post.authorAvatar ? `
              <img src="${this.post.authorAvatar}" alt="${this.post.author}" class="w-16 h-16 rounded-2xl border border-matrix-500 shadow-glow-sm object-cover flex-shrink-0" />
            ` : ''}
            <div class="space-y-1.5 text-center sm:text-left">
              <h3 class="text-sm font-bold text-white flex items-center justify-center sm:justify-start gap-1.5">
                <span>Written by ${this.post.author}</span>
              </h3>
              <p class="text-xs text-terminal-muted leading-relaxed">
                Kim Schulz is a software and hardware engineer based in Denmark, creator of Mastui and author of developer tooling books. Follow his work on <a href="${PROJECT_CONFIG.maintainerUrl}" target="_blank" rel="noopener noreferrer" class="text-matrix-400 hover:underline">schulz.dk</a> and <a href="${PROJECT_CONFIG.githubUrl}" target="_blank" rel="noopener noreferrer" class="text-matrix-400 hover:underline">GitHub</a>.
              </p>
            </div>
          </div>

          <!-- Pagination (Prev / Next Articles) -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            ${prev ? `
              <a 
                href="#/news/${prev.slug}"
                class="p-4 rounded-xl bg-terminal-card border border-terminal-border hover:border-matrix-500/60 shadow-glow-sm group transition-all"
              >
                <div class="text-[11px] text-terminal-muted flex items-center gap-1">
                  <span>← Older Article</span>
                </div>
                <div class="text-xs font-bold text-white group-hover:text-matrix-400 mt-1 line-clamp-1">
                  ${prev.title}
                </div>
              </a>
            ` : `<div></div>`}

            ${next ? `
              <a 
                href="#/news/${next.slug}"
                class="p-4 rounded-xl bg-terminal-card border border-terminal-border hover:border-matrix-500/60 shadow-glow-sm group transition-all text-left sm:text-right sm:col-start-2"
              >
                <div class="text-[11px] text-terminal-muted flex items-center sm:justify-end gap-1">
                  <span>Newer Article →</span>
                </div>
                <div class="text-xs font-bold text-white group-hover:text-matrix-400 mt-1 line-clamp-1">
                  ${next.title}
                </div>
              </a>
            ` : ''}
          </div>

          <!-- Comments & Discussion (Giscus) -->
          <div class="pt-6 border-t border-terminal-border space-y-4">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-base font-bold text-white flex items-center gap-2">
                  ${icons.messageSquare('w-4 h-4 text-matrix-400')}
                  <span>Comments & Discussion</span>
                </h3>
                <p class="text-xs text-terminal-muted mt-0.5">
                  Leave a comment or join the discussion via GitHub Discussions.
                </p>
              </div>

              <a 
                href="${PROJECT_CONFIG.githubDiscussionsUrl}" 
                target="_blank" 
                rel="noopener noreferrer"
                class="text-xs font-semibold text-matrix-400 hover:underline flex items-center gap-1"
              >
                <span>GitHub Discussions</span>
                ${icons.externalLink('w-3 h-3')}
              </a>
            </div>

            <div class="p-6 rounded-2xl bg-terminal-card border border-terminal-border shadow-glow-lg">
              ${this.giscus.render()}
            </div>
          </div>

        </div>
      </div>
    `;
  }

  public bindEvents() {
    const copyBtn = document.getElementById('post-copy-link-btn');
    const copyIcon = document.getElementById('post-copy-icon');
    const copiedIcon = document.getElementById('post-copied-icon');
    const copyText = document.getElementById('post-copy-text');

    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          if (copyIcon && copiedIcon && copyText) {
            copyIcon.classList.add('hidden');
            copiedIcon.classList.remove('hidden');
            copyText.textContent = 'Copied!';
            setTimeout(() => {
              copyIcon.classList.remove('hidden');
              copiedIcon.classList.add('hidden');
              copyText.textContent = 'Copy Link';
            }, 2000);
          }
        } catch {
          // ignore
        }
      });
    }

    if (this.giscus) {
      this.giscus.mount();
    }
  }

  private updateView() {
    const container = document.getElementById('news-post-container');
    if (container) {
      container.outerHTML = this.render();
      this.bindEvents();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  public destroy() {
    if (this.giscus) {
      this.giscus.destroy();
      this.giscus = null;
    }
  }
}
